/**
 * LayoutFactory - Factory component for lazy loading futuristic layouts
 * 
 * This component handles lazy loading of all 15 futuristic layouts
 * to optimize bundle size and initial load time.
 */
import { lazy, Suspense } from 'react';
import { FuturisticLayoutType } from '../../types/layout.types';
import { LoadingLayout } from './LoadingLayout';

// Lazy load all futuristic layouts for performance
const BubbleNavigationLayout = lazy(() => import('./futuristic/BubbleNavigationLayout'));
const OrbitalLayout = lazy(() => import('./futuristic/OrbitalLayout'));
const HexagonalGridLayout = lazy(() => import('./futuristic/HexagonalGridLayout'));
const GlassMorphismLayout = lazy(() => import('./futuristic/GlassMorphismLayout'));
const NeonCyberpunkLayout = lazy(() => import('./futuristic/NeonCyberpunkLayout'));
const AuroraBorealisLayout = lazy(() => import('./futuristic/AuroraBorealisLayout'));
const Isometric3DLayout = lazy(() => import('./futuristic/Isometric3DLayout'));
const ParallaxLayersLayout = lazy(() => import('./futuristic/ParallaxLayersLayout'));
const FloatingCardsLayout = lazy(() => import('./futuristic/FloatingCardsLayout'));
const ParticleFlowLayout = lazy(() => import('./futuristic/ParticleFlowLayout'));
const LiquidMotionLayout = lazy(() => import('./futuristic/LiquidMotionLayout'));
const MatrixRainLayout = lazy(() => import('./futuristic/MatrixRainLayout'));
const HolographicLayout = lazy(() => import('./futuristic/HolographicLayout'));
const CommandCenterLayout = lazy(() => import('./futuristic/CommandCenterLayout'));
const NeuralNetworkLayout = lazy(() => import('./futuristic/NeuralNetworkLayout'));

// Map of layout types to their lazy-loaded components
const futuristicLayoutMap: Partial<Record<FuturisticLayoutType, React.LazyExoticComponent<any>>> = {
  // Navigation Innovation
  BUBBLE_NAVIGATION: BubbleNavigationLayout,
  ORBITAL: OrbitalLayout,
  HEXAGONAL_GRID: HexagonalGridLayout,
  
  // Visual Effects
  GLASS_MORPHISM: GlassMorphismLayout,
  NEON_CYBERPUNK: NeonCyberpunkLayout,
  AURORA_BOREALIS: AuroraBorealisLayout,
  
  // 3D and Depth
  ISOMETRIC_3D: Isometric3DLayout,
  PARALLAX_LAYERS: ParallaxLayersLayout,
  FLOATING_CARDS: FloatingCardsLayout,
  
  // Animations and Particles
  PARTICLE_FLOW: ParticleFlowLayout,
  LIQUID_MOTION: LiquidMotionLayout,
  MATRIX_RAIN: MatrixRainLayout,
  
  // Sci-Fi
  HOLOGRAPHIC: HolographicLayout,
  COMMAND_CENTER: CommandCenterLayout,
  NEURAL_NETWORK: NeuralNetworkLayout,
};

// List of all futuristic layout types
export const FUTURISTIC_LAYOUT_TYPES: FuturisticLayoutType[] = [
  'BUBBLE_NAVIGATION',
  'ORBITAL',
  'HEXAGONAL_GRID',
  'GLASS_MORPHISM',
  'NEON_CYBERPUNK',
  'AURORA_BOREALIS',
  'ISOMETRIC_3D',
  'PARALLAX_LAYERS',
  'FLOATING_CARDS',
  'PARTICLE_FLOW',
  'LIQUID_MOTION',
  'MATRIX_RAIN',
  'HOLOGRAPHIC',
  'COMMAND_CENTER',
  'NEURAL_NETWORK',
];

// Check if a layout type is futuristic
export function isFuturisticLayoutType(layoutType: string): layoutType is FuturisticLayoutType {
  return FUTURISTIC_LAYOUT_TYPES.includes(layoutType as FuturisticLayoutType);
}

interface LayoutFactoryProps {
  layoutType: FuturisticLayoutType;
}

/**
 * LayoutFactory - Renders the appropriate futuristic layout based on type
 * 
 * @param layoutType - The type of futuristic layout to render
 * @returns The lazy-loaded layout component wrapped in Suspense
 */
export function LayoutFactory({ layoutType }: LayoutFactoryProps) {
  const LayoutComponent = futuristicLayoutMap[layoutType];
  
  if (!LayoutComponent) {
    console.warn(`[LayoutFactory] Unknown futuristic layout type: ${layoutType}`);
    return null;
  }
  
  return (
    <Suspense fallback={<LoadingLayout />}>
      <LayoutComponent />
    </Suspense>
  );
}

export default LayoutFactory;
